/**
 * TariffHistoryDrawer — «История изменений». Версии не удаляются (основание
 * для уже проведённых платежей), текущие помечены пилюлей. На десктопе —
 * правый drawer, на мобилке — bottom-sheet; список общий.
 */

import {X} from 'lucide-react';

import type {TariffHistoryItem} from '@services/tariffs';
import {SheetGrabber} from '@components/SheetGrabber';
import {Sheet, SheetContent} from '@components/ui/Sheet';

import {fmtTariffDate} from '../Tariffs.utils';

const KIND_LABEL: Record<TariffHistoryItem['kind'], string> = {
  solo: 'Соло',
  clinic: 'Клиника',
};

type TariffHistoryDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: TariffHistoryItem[];
  mobile?: boolean;
};

export const TariffHistoryDrawer = ({
  open,
  onOpenChange,
  items,
  mobile = false,
}: TariffHistoryDrawerProps) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent
      side={mobile ? 'bottom' : 'right'}
      showClose={false}
      className={
        mobile
          ? 'flex max-h-[80%] flex-col gap-0 rounded-t-[22px] bg-[#F7F8FA] p-0'
          : 'flex w-[460px] max-w-[96vw] flex-col gap-0 bg-[#F7F8FA] p-0'
      }
    >
      <div className="rounded-t-[22px] border-b border-skr-line bg-card px-[22px] pb-[14px] pt-2.5">
        {mobile && <SheetGrabber className="mb-3" />}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-base font-bold">История изменений</div>
            <div className="mt-0.5 text-[12.5px] text-muted-foreground">
              Версии не удаляются · основание для прошлых платежей
            </div>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            aria-label="Закрыть"
            className="grid size-8 place-items-center rounded-[9px] bg-secondary text-muted-foreground"
          >
            <X className="size-[17px]" strokeWidth={2} />
          </button>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-[22px] py-[18px]">
        {items.length === 0 && (
          <div className="py-10 text-center text-[13px] text-muted-foreground">
            Изменений ещё не было — сохраните первую версию тарифа
          </div>
        )}
        {items.map((it) => (
          <div
            key={it.id}
            className={`rounded-xl border bg-card px-4 py-[14px] ${
              it.current ? 'border-skr-info-line' : 'border-skr-line'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[13.5px] font-semibold">
                  {KIND_LABEL[it.kind]} · v{it.version}
                </span>
                {it.current && (
                  <span className="rounded-full bg-accent px-2 py-0.5 text-[11px] font-semibold text-primary">
                    Текущая
                  </span>
                )}
              </div>
              <span className="font-mono text-xs text-muted-foreground">
                {fmtTariffDate(it.date)}
              </span>
            </div>
            <div className="mt-2 font-mono text-[12.5px] text-skr-ink">{it.summary}</div>
          </div>
        ))}
      </div>
    </SheetContent>
  </Sheet>
);
